<template>
  <div class="json-formatter-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <h1 class="hero-title">JSON Formatter & Validator</h1>
            <p class="hero-subtitle">
              Format, validate, and minify JSON data. Perfect for API development, debugging, and code review.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Tool Section -->
    <section class="tool-section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <div class="row">
              <!-- Input -->
              <div class="col-lg-6 mb-4">
                <div class="io-card">
                  <div class="card-header">
                    <h3>JSON Input</h3>
                    <div class="card-actions">
                      <button class="btn-action" title="Clear" @click="clearInput">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                      <button class="btn-action" title="Paste" @click="pasteFromClipboard">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                          <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                        </svg>
                      </button>
                      <button class="btn-action" title="Load sample" @click="loadSample">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <textarea
                    v-model="input"
                    class="io-textarea"
                    :class="{ error: error }"
                    placeholder='Enter JSON here... e.g. {"name": "John", "age": 30}'
                    @input="process"
                  />
                  <div v-if="error" class="status-message error">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {{ error }}
                  </div>
                  <div v-else-if="input && isValid" class="status-message success">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Valid JSON
                  </div>
                  <div v-else class="char-count">{{ input.length }} characters</div>
                </div>
              </div>

              <!-- Output -->
              <div class="col-lg-6 mb-4">
                <div class="io-card">
                  <div class="card-header">
                    <h3>Formatted Output</h3>
                    <div class="card-actions">
                      <select v-model="indentSize" class="indent-selector" @change="process">
                        <option :value="2">2 spaces</option>
                        <option :value="4">4 spaces</option>
                        <option :value="0">Minified</option>
                      </select>
                      <button
                        class="btn-action"
                        :class="{ success: copied }"
                        title="Copy"
                        :disabled="!output"
                        @click="copyOutput"
                      >
                        <svg v-if="!copied" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <pre class="io-output"><code>{{ output || 'Formatted JSON will appear here...' }}</code></pre>
                  <div class="char-count">{{ output.length }} characters</div>
                </div>
              </div>
            </div>

            <!-- JSON Stats -->
            <div v-if="stats" class="stats-card">
              <h4>JSON Statistics</h4>
              <div class="stats-grid">
                <div class="stat-item">
                  <div class="stat-label">Size</div>
                  <div class="stat-value">{{ formatBytes(input.length) }}</div>
                </div>
                <div class="stat-item">
                  <div class="stat-label">Formatted Size</div>
                  <div class="stat-value">{{ formatBytes(output.length) }}</div>
                </div>
                <div class="stat-item">
                  <div class="stat-label">Objects</div>
                  <div class="stat-value">{{ stats.objects }}</div>
                </div>
                <div class="stat-item">
                  <div class="stat-label">Arrays</div>
                  <div class="stat-value">{{ stats.arrays }}</div>
                </div>
                <div class="stat-item">
                  <div class="stat-label">Properties</div>
                  <div class="stat-value">{{ stats.properties }}</div>
                </div>
                <div class="stat-item">
                  <div class="stat-label">Depth</div>
                  <div class="stat-value">{{ stats.depth }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Info Section -->
    <section class="info-section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <div class="info-card">
              <h3>About JSON Formatting</h3>
              <p>
                JSON (JavaScript Object Notation) is a lightweight data interchange format that's easy for humans to read 
                and write, and easy for machines to parse and generate. This tool helps you format, validate, and minify JSON data.
              </p>
              
              <h4>Features</h4>
              <ul>
                <li><strong>Format</strong> - Pretty-print JSON with customizable indentation (2 or 4 spaces)</li>
                <li><strong>Validate</strong> - Check if your JSON is syntactically correct</li>
                <li><strong>Minify</strong> - Remove whitespace to reduce file size</li>
                <li><strong>Statistics</strong> - View object count, array count, depth, and size</li>
              </ul>
              
              <h4>Common Use Cases</h4>
              <ul>
                <li>Debugging API responses</li>
                <li>Preparing JSON for code review</li>
                <li>Optimizing JSON for production (minification)</li>
                <li>Validating configuration files</li>
                <li>Learning JSON structure</li>
              </ul>
              
              <h4>Tips</h4>
              <ul>
                <li>Use 2-space indentation for compact, readable formatting</li>
                <li>Minify JSON for production to reduce bandwidth</li>
                <li>Check the statistics to understand your data structure</li>
                <li>Keep JSON depth reasonable (ideally under 10 levels)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

useHead({
  title: 'JSON Formatter & Validator | Developer Tools',
  meta: [
    { name: 'description', content: 'Format, validate, and minify JSON data. Perfect for API development, debugging, and code review.' }
  ]
})

interface JsonStats {
  objects: number
  arrays: number
  properties: number
  depth: number
}

const input = ref('')
const output = ref('')
const error = ref('')
const copied = ref(false)
const indentSize = ref(2)
const isValid = ref(false)
const stats = ref<JsonStats | null>(null)

const calculateStats = (obj: any, depth = 0): JsonStats => {
  const result: JsonStats = {
    objects: 0,
    arrays: 0,
    properties: 0,
    depth
  }

  if (typeof obj === 'object' && obj !== null) {
    if (Array.isArray(obj)) {
      result.arrays = 1
      obj.forEach(item => {
        const childStats = calculateStats(item, depth + 1)
        result.objects += childStats.objects
        result.arrays += childStats.arrays
        result.properties += childStats.properties
        result.depth = Math.max(result.depth, childStats.depth)
      })
    } else {
      result.objects = 1
      result.properties = Object.keys(obj).length
      Object.values(obj).forEach(value => {
        const childStats = calculateStats(value, depth + 1)
        result.objects += childStats.objects
        result.arrays += childStats.arrays
        result.properties += childStats.properties
        result.depth = Math.max(result.depth, childStats.depth)
      })
    }
  }

  return result
}

const process = () => {
  error.value = ''
  isValid.value = false
  stats.value = null

  if (!input.value.trim()) {
    output.value = ''
    return
  }

  try {
    const parsed = JSON.parse(input.value)
    isValid.value = true
    
    if (indentSize.value === 0) {
      output.value = JSON.stringify(parsed)
    } else {
      output.value = JSON.stringify(parsed, null, indentSize.value)
    }
    
    stats.value = calculateStats(parsed)
  } catch (e: any) {
    error.value = `Invalid JSON: ${e.message}`
    output.value = ''
  }
}

const clearInput = () => {
  input.value = ''
  output.value = ''
  error.value = ''
  isValid.value = false
  stats.value = null
}

const pasteFromClipboard = async () => {
  try {
    const text = await navigator.clipboard.readText()
    input.value = text
    process()
  } catch (err) {
    console.error('Failed to paste:', err)
  }
}

const copyOutput = async () => {
  if (!output.value) return
  
  try {
    await navigator.clipboard.writeText(output.value)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

const loadSample = () => {
  input.value = `{
  "name": "John Doe",
  "age": 30,
  "email": "john@example.com",
  "address": {
    "street": "123 Main St",
    "city": "San Francisco",
    "state": "CA",
    "zip": "94102"
  },
  "hobbies": ["reading", "coding", "hiking"],
  "isActive": true,
  "metadata": {
    "createdAt": "2024-01-15T10:30:00Z",
    "updatedAt": "2024-01-20T15:45:00Z"
  }
}`
  process()
}

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${Math.round(bytes / Math.pow(k, i) * 100) / 100} ${sizes[i]}`
}
</script>

<style scoped>
.json-formatter-page {
  padding-top: 80px;
  min-height: 100vh;
  background: linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%);
}

.hero-section {
  padding: 4rem 0 2rem;
  text-align: center;
}

.hero-title {
  font-size: 3rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.25rem;
  color: #4a5568;
  max-width: 700px;
  margin: 0 auto;
}

.tool-section {
  padding: 2rem 0 4rem;
}

.io-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  height: 100%;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.card-header h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.indent-selector {
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #4a5568;
  cursor: pointer;
  background: white;
}

.indent-selector:focus {
  outline: none;
  border-color: #667eea;
}

.btn-action {
  background: white;
  border: 1px solid #e2e8f0;
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #4a5568;
}

.btn-action:hover:not(:disabled) {
  border-color: #667eea;
  color: #667eea;
  background: #f7fafc;
}

.btn-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-action.success {
  border-color: #48bb78;
  color: #48bb78;
  background: #f0fff4;
}

.io-textarea {
  width: 100%;
  min-height: 400px;
  padding: 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-size: 0.95rem;
  resize: vertical;
  transition: all 0.2s ease;
}

.io-textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.io-textarea.error {
  border-color: #f56565;
}

.io-output {
  width: 100%;
  min-height: 400px;
  max-height: 600px;
  padding: 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  background: #f7fafc;
  overflow: auto;
  margin: 0;
}

.io-output code {
  font-family: 'Courier New', monospace;
  font-size: 0.95rem;
  color: #2d3748;
  white-space: pre;
}

.char-count {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #a0aec0;
  text-align: right;
}

.status-message {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 6px;
}

.status-message.error {
  color: #f56565;
  background: #fff5f5;
}

.status-message.success {
  color: #48bb78;
  background: #f0fff4;
}

.stats-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-top: 2rem;
}

.stats-card h4 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 1rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
}

.stat-item {
  text-align: center;
  padding: 1rem;
  background: #f7fafc;
  border-radius: 8px;
}

.stat-label {
  font-size: 0.875rem;
  color: #718096;
  margin-bottom: 0.5rem;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #667eea;
}

.info-section {
  padding: 4rem 0;
  background: white;
}

.info-card {
  padding: 2rem;
}

.info-card h3 {
  color: #1a202c;
  font-weight: 700;
  margin-bottom: 1rem;
}

.info-card h4 {
  color: #2d3748;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

.info-card p {
  color: #4a5568;
  line-height: 1.7;
}

.info-card ul {
  color: #4a5568;
  line-height: 1.8;
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 2rem;
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
